import { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCcw, Upload, CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Self-healing wizard that detects image references in `theme_settings`
 * and `entities` pointing at the legacy ephemeral `/api/uploads/...` path
 * for files that no longer exist on disk. Lets the admin replace each
 * broken reference with a fresh upload (now stored in persistent object
 * storage) in one click.
 */
export const BrokenImageWizard = ({ token }) => {
  const [scanning, setScanning] = useState(false);
  const [items, setItems] = useState([]);
  const [scanned, setScanned] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  const scan = useCallback(async () => {
    setScanning(true);
    try {
      const res = await axios.get(`${API}/admin/scan-broken-images`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.items || []);
      setScanned(true);
      const count = res.data.broken_count || 0;
      if (count === 0) {
        toast.success('No broken images found — production looks healthy');
      } else {
        toast.warning(`Found ${count} broken image reference${count === 1 ? '' : 's'}`);
      }
    } catch (e) {
      console.error('Scan failed:', e);
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  }, [token]);

  const replace = useCallback(
    async (item, file) => {
      if (!file) return;
      const itemKey = itemId(item);
      setUploadingId(itemKey);
      try {
        // Step 1: upload to persistent storage
        const formData = new FormData();
        formData.append('file', file);
        const upRes = await axios.post(`${API}/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
        const newUrl = process.env.REACT_APP_BACKEND_URL + upRes.data.url;

        // Step 2: rewrite the DB reference atomically
        await axios.post(
          `${API}/admin/replace-broken-image`,
          {
            source: item.source,
            field: item.field,
            new_url: newUrl,
            entity_id: item.entity_id,
            gallery_index: item.gallery_index,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        // Optimistically drop the item from the list
        setItems((prev) => prev.filter((i) => itemId(i) !== itemKey));
        toast.success(`Replaced "${item.label}"`);
      } catch (e) {
        console.error('Replace failed:', e);
        toast.error(e.response?.data?.detail || 'Replace failed');
      } finally {
        setUploadingId(null);
      }
    },
    [token],
  );

  return (
    <div className="rounded-xl border border-border p-6 space-y-4" data-testid="broken-image-wizard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Re-upload Broken Images
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Scans theme settings and partner records for image references
            that point at wiped ephemeral storage. Re-upload each one to
            persistent storage in a single click.
          </p>
        </div>
        <Button
          onClick={scan}
          disabled={scanning}
          variant="outline"
          data-testid="broken-image-wizard-scan-btn"
        >
          <RefreshCcw className={`h-4 w-4 mr-2 ${scanning ? 'animate-spin' : ''}`} />
          {scanning ? 'Scanning…' : 'Scan now'}
        </Button>
      </div>

      {scanned && items.length === 0 && (
        <div
          className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-3"
          data-testid="broken-image-wizard-empty"
        >
          <CheckCircle2 className="h-4 w-4" />
          All image references resolve. Nothing to fix.
        </div>
      )}

      {items.length > 0 && (
        <ul className="divide-y divide-border border border-border rounded-lg overflow-hidden">
          {items.map((item) => {
            const itemKey = itemId(item);
            const isUploading = uploadingId === itemKey;
            return (
              <li
                key={itemKey}
                className="p-4 flex items-center gap-4 flex-wrap"
                data-testid={`broken-image-row-${itemKey}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{item.label}</div>
                  <div className="text-xs text-muted-foreground truncate font-mono">
                    {item.url}
                  </div>
                </div>
                <label className="shrink-0">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={isUploading}
                    onChange={(e) => e.target.files[0] && replace(item, e.target.files[0])}
                    data-testid={`broken-image-replace-input-${itemKey}`}
                  />
                  <span
                    className={`inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 h-9 text-sm font-medium cursor-pointer hover:bg-accent ${
                      isUploading ? 'opacity-50 pointer-events-none' : ''
                    }`}
                  >
                    <Upload className="h-4 w-4" />
                    {isUploading ? 'Uploading…' : 'Re-upload'}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

function itemId(item) {
  if (item.source === 'theme') return `theme-${item.field}`;
  if (item.source === 'entity_gallery')
    return `entity-${item.entity_id}-gallery-${item.gallery_index}`;
  return `entity-${item.entity_id}-${item.field}`;
}

export default BrokenImageWizard;
